import React, {useEffect, useState} from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import {useParams} from "react-router-dom";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import {baseUrl, formatDateTime, ipAddress, userLocation, userName} from "../../Univ.js";
function NurseNotes() {
    const {ip_id} = useParams();
    // ---------------------------------------------------------
    // State
    // ---------------------------------------------------------
    const [ipData, setIpData] = useState([]);
    const [notesData, setNotesData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDateTime, setSelectedDateTime] = useState(
        new Date()
    );
    // Single source of truth for typed + speech notes
    const [notesText, setNotesText] = useState("");
    // Speech recognition
    const {
        transcript,
        resetTranscript,
        listening,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition({
        continuous: true
    });
    // ---------------------------------------------------------
    // Fetch IP Admission
    // ---------------------------------------------------------
    useEffect(() => {
        fetchIpData();
    }, [ip_id]);
    const fetchIpData = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${baseUrl}edit-ip-admit/${ip_id}/`
            );
            setIpData([response.data]);
        } catch (error) {
            console.error(
                "Failed to fetch IP data:",
                error
            );
            Swal.fire(
                "Error",
                "Unable to load patient information.",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    };
    // ---------------------------------------------------------
    // Fetch Nurse Notes History
    // ---------------------------------------------------------
    useEffect(() => {
        fetchNotesData();
    }, [ip_id]);
    const fetchNotesData = async () => {
        try {
            const response = await axios.get(
                `${baseUrl}view-nurse-notes/${ip_id}/`
            );
            setNotesData(
                Array.isArray(response.data)
                    ? response.data
                    : []
            );
        } catch (error) {
            console.error(
                "Failed to fetch nurse notes:",
                error
            );
        }
    };
    // ---------------------------------------------------------
    // Speech Recognition
    // ---------------------------------------------------------
    /*
     * When speech recognition produces a transcript,
     * append it to the existing textarea content.
     *
     * The textarea itself remains editable.
     */
    useEffect(() => {
        if (!transcript) {
            return;
        }
        setNotesText((previousText) => {
            const currentText = previousText.trim();
            if (!currentText) {
                return transcript.trim();
            }
            return `${currentText} ${transcript.trim()}`;
        });
        // Clear transcript after adding it to notesText.
        resetTranscript();
    }, [transcript, resetTranscript]);
    // ---------------------------------------------------------
    // Start Speech
    // ---------------------------------------------------------
    const handleStartListening = () => {
        if (!browserSupportsSpeechRecognition) {
            Swal.fire(
                "Not Supported",
                "Speech recognition is not supported in this browser.",
                "warning"
            );
            return;
        }
        SpeechRecognition.startListening({
            continuous: true
        });
    };
    // ---------------------------------------------------------
    // Stop Speech
    // ---------------------------------------------------------
    const handleStopListening = () => {
        SpeechRecognition.stopListening();
    };
    // ---------------------------------------------------------
    // Clear Notes
    // ---------------------------------------------------------
    const handleClearText = () => {
        SpeechRecognition.stopListening();
        resetTranscript();
        setNotesText("");
    };
    // ---------------------------------------------------------
    // Download Notes
    // ---------------------------------------------------------
    const handleDownload = () => {
        if (!notesText.trim()) {
            Swal.fire(
                "No Notes",
                "There is no text to download.",
                "warning"
            );
            return;
        }
        const filename = "nurse-notes.txt";
        const element = document.createElement("a");
        element.setAttribute(
            "href",
            "data:text/plain;charset=utf-8," +
            encodeURIComponent(notesText)
        );
        element.setAttribute(
            "download",
            filename
        );
        element.style.display = "none";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };
    // ---------------------------------------------------------
    // Date / Time
    // ---------------------------------------------------------
    const handleDateTimeChange = (datetime) => {
        if (datetime) {
            setSelectedDateTime(datetime);
        }
    };
    // ---------------------------------------------------------
    // Submit Nurse Note
    // ---------------------------------------------------------
    const formSubmit = async (event) => {
        event.preventDefault();
        const notes = notesText.trim();
        if (!notes) {
            Swal.fire({
                title: "Notes Required",
                text: "Please type or dictate some nurse notes.",
                icon: "warning",
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }
        try {
            // Stop speech before submitting
            SpeechRecognition.stopListening();
            const formData = new FormData();
            formData.append(
                "ip",
                ip_id
            );
            formData.append(
                "notes",
                notes
            );
            /*
             * Convert Date object into a format Django can
             * easily process.
             */
            const notesTime =
                selectedDateTime instanceof Date
                    ? selectedDateTime.toISOString()
                    : selectedDateTime;
            formData.append(
                "notes_time",
                notesTime
            );
            formData.append(
                "status",
                "1"
            );
            formData.append(
                "addedby",
                userName
            );
            formData.append(
                "ip_add",
                ipAddress
            );
            formData.append(
                "location",
                userLocation
            );
            const response = await axios.post(
                `${baseUrl}nurse-notes-create/`,
                formData
            );
            if (
                response.status === 200 ||
                response.status === 201
            ) {
                Swal.fire({
                    title: "Notes Created Successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                // Clear local state
                setNotesText("");
                resetTranscript();
                // Return to patient page
                setTimeout(() => {
                    window.location.href =
                        `/nurse-pat-view/${ip_id}/`;
                }, 1500);
            }
        } catch (error) {
            console.error(
                "Failed to create nurse notes:",
                error
            );
            Swal.fire(
                "Error!",
                "Failed to save nurse notes. Please try again later.",
                "error"
            );
        }
    };
    // ---------------------------------------------------------
    // Delete Nurse Note
    // ---------------------------------------------------------
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Confirm Delete",
            text: "Are you sure you want to delete this note?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel"
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(
                `${baseUrl}delete-nurse-notes/${id}/`
            );
            await Swal.fire({
                title: "Deleted",
                text: "Nurse note has been deleted.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });
            // Refresh current page
            window.location.href =
                `/nurse-notes/${ip_id}/`;
        } catch (error) {
            console.error(
                "Failed to delete nurse note:",
                error
            );
            Swal.fire(
                "Error",
                "Data has not been deleted.",
                "error"
            );
        }
    };
    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------
    return (
        <section className="container mt-4">

            {/* =====================================================
                PRIMARY CONSULTANT
            ====================================================== */}

            <h3
                className="text-center"
                style={{
                    color: "white",
                    backgroundColor: "#0dcaf0"
                }}
            >
                <i
                    className="fa-solid fa-stethoscope"
                    aria-hidden="true"
                    style={{color: "red"}}
                ></i>

                {" Primary Consultant - Dr. "}

                {ipData.length > 0 &&
                    ipData[0]?.primary_consult?.consultant
                }
            </h3>


            {/* =====================================================
                PATIENT INFORMATION
            ====================================================== */}

            <div className="table-responsive">

                <table className="erp-table-modern">

                    <thead>

                    <tr
                        style={{
                            backgroundColor: "#1bc5ce",
                            color: "white"
                        }}
                    >

                        <th>Patient</th>
                        <th>IP ID</th>
                        <th>Patient ID</th>
                        <th>Patient Name</th>
                        <th>Gender</th>
                        <th>D.O.B/Age</th>
                        <th>Mobile</th>
                        <th>Regd. On</th>

                    </tr>

                    </thead>


                    <tbody>

                    {ipData.map(
                        (patientData) => (
                            <tr key={patientData.ip_id}>

                                <td>

                                    <img
                                        src={
                                            patientData.profile_pic
                                        }
                                        alt="Patient"
                                        className="thumbnail"
                                        style={{
                                            width: 120,
                                            height: 60,
                                            objectFit: "cover"
                                        }}
                                    />

                                </td>


                                <td>
                                    {patientData.ip_id}
                                </td>


                                <td>
                                    {patientData.pat?.pat_id}
                                </td>


                                <td>
                                    {patientData.pat?.patient}
                                </td>


                                <td>
                                    {patientData.pat?.gender}
                                </td>


                                <td>
                                    {patientData.pat?.age}
                                </td>


                                <td>
                                    {patientData.pat?.mobile}
                                </td>


                                <td>
                                    {formatDateTime(
                                        patientData.admit_date
                                    )}
                                </td>

                            </tr>
                        )
                    )}

                    </tbody>

                </table>

            </div>


            {/* =====================================================
                NURSE NOTES
            ====================================================== */}

            <div className="mt-4">

                <h3
                    className="text-center"
                    style={{
                        color: "white",
                        backgroundColor: "#0dcaf0"
                    }}
                >

                    <i
                        className="fa-solid fa-user-nurse"
                        aria-hidden="true"
                        style={{color: "red"}}
                    ></i>

                    {" Nurse Notes"}

                </h3>


                <form onSubmit={formSubmit}>

                    <div className="row">


                        {/* =================================================
                            NOTES TEXTAREA
                        ================================================== */}

                        <div className="col-md-7">

                            <label
                                htmlFor="notes"
                                className="form-label"
                            >
                                Notes:
                            </label>


                            <textarea
                                className="no-comma form-control"
                                tabIndex="0"
                                name="notes"
                                id="notes"
                                rows={8}
                                value={notesText}
                                onChange={(event) =>
                                    setNotesText(
                                        event.target.value
                                    )
                                }
                                placeholder="Type your notes here or use the microphone..."
                                autoComplete="off"
                            />


                            {/* =================================================
                                SPEECH STATUS
                            ================================================== */}

                            <div
                                className="mt-2"
                                style={{
                                    fontSize: "13px",
                                    fontWeight: 600
                                }}
                            >

                                {listening ? (
                                    <span
                                        style={{
                                            color: "#dc3545"
                                        }}
                                    >
                                        <i className="fa fa-circle"></i>
                                        {" Listening... Speak now"}
                                    </span>
                                ) : (
                                    <span
                                        style={{
                                            color: "#6c757d"
                                        }}
                                    >
                                        <i className="fa fa-microphone-slash"></i>
                                        {" Microphone stopped"}
                                    </span>
                                )}

                            </div>


                            {/* =================================================
                                BUTTONS
                            ================================================== */}

                            <div className="mt-3">

                                {/* STOP */}

                                <button
                                    type="button"
                                    className="btn btn-danger me-2"
                                    onClick={
                                        handleStopListening
                                    }
                                    disabled={!listening}
                                >

                                    <i className="fa fa-microphone-lines-slash me-1"></i>

                                    Stop

                                </button>


                                {/* START */}

                                <button
                                    type="button"
                                    className="btn btn-success me-2"
                                    onClick={
                                        handleStartListening
                                    }
                                    disabled={listening}
                                >

                                    <i className="fa fa-microphone-lines me-1"></i>

                                    Speak

                                </button>


                                {/* CLEAR */}

                                <button
                                    type="button"
                                    className="btn btn-warning me-2"
                                    onClick={
                                        handleClearText
                                    }
                                    disabled={!notesText}
                                >

                                    <i className="fa fa-eraser me-1"></i>

                                    Clear

                                </button>


                                {/* DOWNLOAD */}

                                <button
                                    type="button"
                                    className="btn btn-info"
                                    onClick={
                                        handleDownload
                                    }
                                    disabled={!notesText}
                                >

                                    <i className="fa fa-download me-1"></i>

                                    Download

                                </button>

                            </div>

                        </div>


                        {/* =================================================
                            NOTES DATE / TIME
                        ================================================== */}

                        <div className="col-md-3">

                            <label
                                htmlFor="notes_time"
                                className="form-label"
                            >
                                Notes Time:
                            </label>


                            <DatePicker
                                className="form-control"
                                name="notes_time"
                                id="notes_time"
                                selected={
                                    selectedDateTime
                                }
                                onChange={
                                    handleDateTimeChange
                                }
                                dateFormat="yyyy-MM-dd h:mm aa"
                                showTimeSelect
                                timeIntervals={15}
                                timeCaption="Time"
                            />

                        </div>

                    </div>


                    {/* =====================================================
                        SUBMIT
                    ====================================================== */}

                    <div
                        className="d-flex justify-content-center mt-4"
                    >

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!notesText.trim()}
                        >

                            <i className="fa fa-save me-2"></i>

                            Submit Nurse Notes

                        </button>

                    </div>

                </form>


                {/* =====================================================
                    NURSE NOTES HISTORY
                ====================================================== */}

                <div className="card card-body mt-4">

                    <h3
                        className="text-center"
                        style={{
                            color: "white",
                            backgroundColor: "#0dcaf0"
                        }}
                    >

                        <i
                            className="fa-solid fa-user-nurse"
                            aria-hidden="true"
                            style={{color: "red"}}
                        ></i>

                        {" Nurse Notes History "}

                        <i className="fa-brands fa-discourse"></i>

                    </h3>


                    <div className="table-responsive">

                        <table className="erp-table-modern">

                            <thead>

                            <tr
                                style={{
                                    backgroundColor: "#1bc5ce",
                                    color: "white"
                                }}
                            >

                                <th>Date</th>
                                <th>Notes</th>
                                <th>Added By</th>
                                <th>Delete</th>

                            </tr>

                            </thead>


                            <tbody>

                            {notesData.length > 0 ? (
                                notesData.map(
                                    (note) => (
                                        <tr key={note.id}>

                                            <td>
                                                {formatDateTime(
                                                    note.notes_time
                                                )}
                                            </td>


                                            <td
                                                style={{
                                                    whiteSpace:
                                                        "pre-wrap"
                                                }}
                                            >
                                                {note.notes}
                                            </td>


                                            <td>
                                                {note.addedby ||
                                                    "-"}
                                            </td>


                                            <td>

                                                <button
                                                    className="btn btn-danger"
                                                    type="button"
                                                    onClick={() =>
                                                        handleDelete(
                                                            note.id
                                                        )
                                                    }
                                                >

                                                    <i className="fa fa-trash me-1"></i>

                                                    Delete

                                                </button>

                                            </td>

                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>

                                    <td
                                        colSpan="4"
                                        className="text-center"
                                    >
                                        No nurse notes found.
                                    </td>

                                </tr>
                            )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>

        </section>
    );
}
export default NurseNotes;
